import { useState, useEffect } from 'react'
import { Table, Button, Space, Tag, message, Modal, Descriptions, List, Card } from 'antd'
import { DownloadOutlined, UploadOutlined, EyeOutlined, CheckCircleOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { assignmentApi, submissionApi } from '../../api/auth'

interface Assignment {
  id: number
  title: string
  description: string
  deadline: string
  total_score: number
  status?: string
  score?: number
  submission_id?: number
}

interface Submission {
  id: number
  assignment_id: number
  score: number
  feedback: string
  status: string
  submitted_at: string
  graded_at: string
}

export default function AssignmentList() {
  const [assignments, setAssignments] = useState<Assignment[]>([])
  const [loading, setLoading] = useState(false)
  const [feedbackModal, setFeedbackModal] = useState(false)
  const [currentFeedback, setCurrentFeedback] = useState<any>(null)
  const navigate = useNavigate()

  useEffect(() => {
    fetchAssignments()
  }, [])

  const fetchAssignments = async () => {
    setLoading(true)
    try {
      // 获取题目列表
      const data = await assignmentApi.list()
      const assignmentsList = data.assignments || []

      // 获取提交记录
      const submissionsData = await submissionApi.list()
      const submissions = submissionsData.submissions || []

      // 合并状态信息
      const mergedAssignments = assignmentsList.map((assignment: Assignment) => {
        const submission = submissions.find((s: Submission) => s.assignment_id === assignment.id)
        if (submission) {
          return {
            ...assignment,
            status: submission.status === 'graded' ? 'completed' : 'pending',
            score: submission.score,
            submission_id: submission.id
          }
        }
        return {
          ...assignment,
          status: 'pending'
        }
      })

      setAssignments(mergedAssignments)
    } catch (error) {
      message.error('获取作业列表失败')
    } finally {
      setLoading(false)
    }
  }

  const handleDownload = async (id: number) => {
    try {
      const blob = await assignmentApi.downloadTemplate(id)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `assignment_${id}.xlsx`
      a.click()
      window.URL.revokeObjectURL(url)
      message.success('下载成功')
    } catch (error) {
      message.error('下载失败')
    }
  }

  const handleViewFeedback = async (submissionId: number) => {
    try {
      const submission = await submissionApi.get(submissionId)
      const feedback = JSON.parse(submission.feedback || '[]')
      setCurrentFeedback({
        score: submission.score,
        feedback,
        submitted_at: submission.submitted_at,
        graded_at: submission.graded_at
      })
      setFeedbackModal(true)
    } catch (error) {
      message.error('获取反馈失败')
    }
  }

  const columns = [
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title'
    },
    {
      title: '总分',
      dataIndex: 'total_score',
      key: 'total_score'
    },
    {
      title: '截止日期',
      dataIndex: 'deadline',
      key: 'deadline',
      render: (date: string) => date ? new Date(date).toLocaleString() : '未设置'
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const color = status === 'completed' ? 'green' : 'orange'
        const text = status === 'completed' ? '已完成' : '待完成'
        return <Tag color={color}>{text}</Tag>
      }
    },
    {
      title: '得分',
      dataIndex: 'score',
      key: 'score',
      render: (score: number, record: Assignment) => {
        if (record.status === 'completed' && score !== undefined) {
          return <span style={{ color: '#52c41a', fontWeight: 'bold' }}>{score}分</span>
        }
        return '-'
      }
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: Assignment) => (
        <Space>
          <Button
            icon={<DownloadOutlined />}
            size="small"
            onClick={() => handleDownload(record.id)}
          >
            下载
          </Button>
          {record.status !== 'completed' && (
            <Button
              type="primary"
              icon={<UploadOutlined />}
              size="small"
              onClick={() => navigate(`/assignments/${record.id}/submit`)}
            >
              提交
            </Button>
          )}
          {record.status === 'completed' && record.submission_id && (
            <Button
              type="default"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleViewFeedback(record.submission_id!)}
            >
              查看反馈
            </Button>
          )}
        </Space>
      )
    }
  ]

  return (
    <div>
      <h2 style={{ marginBottom: 16 }}>我的作业</h2>
      <Table
        columns={columns}
        dataSource={assignments}
        rowKey="id"
        loading={loading}
      />

      <Modal
        title="评分反馈"
        open={feedbackModal}
        onCancel={() => setFeedbackModal(false)}
        footer={[
          <Button key="close" onClick={() => setFeedbackModal(false)}>
            关闭
          </Button>
        ]}
        width={800}
      >
        {currentFeedback && (
          <div>
            <Card style={{ marginBottom: 16 }}>
              <Descriptions column={2}>
                <Descriptions.Item label="得分">
                  <span style={{ fontSize: 24, color: '#52c41a', fontWeight: 'bold' }}>
                    {currentFeedback.score}分
                  </span>
                </Descriptions.Item>
                <Descriptions.Item label="提交时间">
                  {new Date(currentFeedback.submitted_at).toLocaleString()}
                </Descriptions.Item>
                <Descriptions.Item label="评分时间">
                  {new Date(currentFeedback.graded_at).toLocaleString()}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            <Card title="详细反馈">
              <List
                dataSource={currentFeedback.feedback}
                renderItem={(item: any) => (
                  <List.Item>
                    <Space>
                      {item.status === 'correct' ? (
                        <CheckCircleOutlined style={{ color: '#52c41a' }} />
                      ) : (
                        <CheckCircleOutlined style={{ color: '#ff4d4f' }} />
                      )}
                      <span>
                        单元格 <strong>{item.cell}</strong>: {item.message}
                      </span>
                    </Space>
                  </List.Item>
                )}
              />
            </Card>
          </div>
        )}
      </Modal>
    </div>
  )
}
